import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AuthStatus } from 'src/app/@enums/authentication/auth-status.enum';
import { LOGIN } from 'src/app/@graphql/operations/mutation';
import { ApiService } from 'src/app/@graphql/services/api.service';
import { IUser } from 'src/app/@interfaces/user.interface';
import { UsersService } from 'src/app/@pages/users/users.service';
import { CookieAuthService } from '../storage-variables/cookie-auth.service';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { clearCompanies } from 'src/app/state/actions/user-companies.actions';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {
  private _accesMenu = new BehaviorSubject<boolean | null>(null);
  public _accesMenu$ = this._accesMenu.asObservable();
  private unsubscribe$ = new Subject<void>();

  private _currentUser: BehaviorSubject<IUser | undefined> =
    new BehaviorSubject<IUser | undefined>(undefined);
  private _authStatus: BehaviorSubject<AuthStatus | undefined> =
    new BehaviorSubject<AuthStatus | undefined>(undefined);
  public currentUser$: Observable<IUser | undefined> =
    this._currentUser.asObservable();
  public authStatus$: Observable<AuthStatus | undefined> =
    this._authStatus.asObservable();

  constructor(
    apollo: Apollo,
    private router: Router,
    private userService: UsersService,
    private cookieAuthService: CookieAuthService,
    private cookieService: CookieService,
    private spinnerService: NgxSpinnerService,
    private store: Store<AppState>,
  ) {
    super(apollo);
  }

  private syncroAuthValues(user: IUser | undefined, status: AuthStatus) {
    this._currentUser.next(user);
    this._authStatus.next(status);
  }

  async checkAuthStatus() {
    const jwt = this.cookieAuthService.getToken;
    if (!jwt) {
      this.router.navigateByUrl('/login');
      return;
    }
    await this.userService
      .me()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (res) => {
          await this.syncroAuthValues(res, AuthStatus.authenticated);
          if (this.router.url === '/login' || this.router.url === '/') {
            return this.router.navigateByUrl(
              await this.userService.getInitialRouteByRole(),
            );
          }
          return;
        },
        error: () => {
          this.logOut();
        },
      });
    return;
  }

  login(email: string, password: string) {
    return this.mutation(LOGIN, { email, password }).pipe(
      map((result: any) => {
        const now = new Date();
        now.setHours(now.getHours() + 8);
        this._currentUser.next(result.login.user);
        this._authStatus.next(AuthStatus.authenticated);
        return result;
      }),
    );
  }

  logOut() {
    // Clear service variables FIRST before anything else
    this.cookieAuthService.resetAuthVariables();

    // Clear company data specifically
    this.cookieAuthService.clearCompanyData();

    // Clear store
    this.store.dispatch(clearCompanies());

    // Clear storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error('Error clearing storage:', e);
    }

    // Clear cookies - exhaustive deletion
    try {
      const cookiesToDelete = ['userIds', 'companyItems'];
      const paths = ['/', ''];
      const domains = [
        window.location.hostname,
        '.' + window.location.hostname,
        '',
      ];

      cookiesToDelete.forEach((cookieName) => {
        paths.forEach((path) => {
          domains.forEach((domain) => {
            try {
              this.cookieService.delete(cookieName, path, domain);
              this.cookieService.delete(cookieName, path);
            } catch (e) {
              // Continue trying other combinations
            }
          });
        });
        // One more attempt without parameters
        this.cookieService.delete(cookieName);
      });

      // Delete ALL cookies as final measure
      const allCookies = this.cookieService.getAll();
      Object.keys(allCookies).forEach((cookieName) => {
        paths.forEach((path) => {
          this.cookieService.delete(cookieName, path);
        });
        this.cookieService.delete(cookieName);
      });
    } catch (e) {
      console.error('Error clearing cookies:', e);
    }

    // Clear auth values
    this.syncroAuthValues(undefined, AuthStatus.notAuthenticated);
    this.spinnerService.hide();

    // Cleanup subscriptions
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$ = new Subject<void>();

    // Force a complete page reload to clear all in-memory state
    // Use setTimeout to ensure all cleanup completes first
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }
}
